import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import 'moment/locale/pt-br';
import get from 'lodash/get';

import {
  Button,
  Form,
  Drawer,
  Modal,
  Tabs,
} from 'antd';

import { Cod } from '~/components/UI';
import * as actions from '../actions';
import Details from './Details';
import Cadences from './Cadences';
import InstagramThread from './InstagramThread';
import { COLUMN_IDS } from '../constants';

// Helper function to get the correct column_id for backend
const getColumnIdForBackend = (columnId, data) => {
  // If it's a step column (starts with 'step_'), we need to determine the correct column_id
  if (columnId && typeof columnId === 'string' && columnId.startsWith('step_')) {
    // Check if the lead has explicit column_id set to Positivo or Negativo
    if (data.column_id === COLUMN_IDS.POSITIVE) return COLUMN_IDS.POSITIVE;
    if (data.column_id === COLUMN_IDS.NEGATIVE) return COLUMN_IDS.NEGATIVE;
    
    // For step columns, check the cadence progress to determine the correct column
    if (data.additional_data && data.additional_data.cadences) {
      const cadences = data.additional_data.cadences;
      for (const cadence of cadences) {
        if (cadence.data && Array.isArray(cadence.data)) {
          // Check if any tasks are done
          const hasCompletedTasks = cadence.data.some(task => task.status === 'done');
          
          if (!hasCompletedTasks) {
            // If no tasks are done yet, put in LEADS column
            return COLUMN_IDS.LEADS;
          }
          
          // If has completed tasks, put in LEADS column (since it's not explicitly in Positivo/Negativo)
          return COLUMN_IDS.LEADS;
        }
      }
    }
    
    // Default to LEADS column if no cadence data or other conditions
    return COLUMN_IDS.LEADS;
  }
  
  // For regular columns (Positivo, Negativo, etc.), send the column_id as is
  return columnId;
};

const ProspectingDrawer = ({
  form,
  selectedCadence,
}) => {
  const dispatch = useDispatch();

  const {
    drawer,
    columns,
  } = useSelector(state => state.socialSelling);
  const data = drawer.data;
  const loading = drawer.saving;

  const handleCreate = () => {
    const { data } = drawer;
    form.validateFields((err, values) => {
      if (err) {
        return;
      }
      const { name, column_id, ...restValues } = values;
      // Get the correct column_id for backend
      const backendColumnId = getColumnIdForBackend(column_id, data);
      dispatch(actions.create({
        additional_data: {
          ...get(data, 'additional_data', {}),
          ...restValues,
        },
        name: values.name,
        entity_type: 'socialSelling',
        column_id: backendColumnId,
        board_id: 1,
      }))
      .catch(() => {
        // Error handling is done in the reducer (CREATE.FAIL sets saving to false)
      });
    });
  };

  const handleSave = () => {
    form.validateFields((err, values) => {
      if (err) {
        return;
      }
      const { name, column_id, ...restValues } = values;
      // Get the correct column_id for backend
      const backendColumnId = getColumnIdForBackend(column_id, data);
      dispatch(actions.update(get(data, 'id'), {
        additional_data: {
          ...get(data, 'additional_data', {}),
          ...restValues,
        },
        name: values.name,
        column_id: backendColumnId,
      }))
      .catch(() => {
        // Error handling is done in the reducer (SAVE.FAIL sets saving to false)
      });
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Você realmente deseja excluir este lead?',
      content: 'Esta ação não poderá ser revertida.',
      okText: 'Sim',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => dispatch(actions.remove(data))
    });
  }

  const closeDrawerLeads = () => {
    form.resetFields();
    dispatch(actions.close());
  }

  var instagramHandle = data && (data.instagram || (data.additional_data && data.additional_data.instagram));
  var showInstagram = true;

  return (
    <Drawer
      title={get(data, 'id') ? <span>Editar Lead <Cod float="right">#CRM{get(data, 'id')}</Cod></span> : 'Cadastrar Lead'}
      width={showInstagram ? 1280 : 800}
      placement="right"
      onClose={closeDrawerLeads}
      maskClosable={false}
      visible={get(drawer, 'show', false)}
      destroyOnClose={true}
      style={{
        height: '100%',
        overflow: 'hidden',
        paddingBottom: 53,
      }}
      bodyStyle={{ padding: 0, height: '100%' }}
    >
      <div style={{ display: 'flex', height: 'calc(100vh - 55px - 53px)', gap: 0 }}>
        {/* Left: details/cadence tabs */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 24, borderRight: '1px solid #f0f0f0' }}>
          <Tabs defaultActiveKey="1" type='card'>
            <Tabs.TabPane tab="Detalhes" key="1">
              <Details
                data={data}
                columns={columns}
                form={form}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Cadência" key="2">
              <Cadences
                data={data}
                form={form}
                selectedCadence={selectedCadence}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>

        {/* Right: Instagram thread — visible to anyone with access to this page */}
        {showInstagram && (
          <div style={{ width: 440, flexShrink: 0, background: '#fafafa', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <InstagramThread instagramHandle={instagramHandle} />
          </div>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          borderTop: '1px solid #e8e8e8',
          padding: '10px 16px',
          textAlign: 'right',
          left: 0,
          background: '#fff',
          borderRadius: '0 0 4px 4px',
        }}
      >
        {get(data, 'id') && (<Button onClick={handleDelete} type="danger" style={{ marginRight: '8px' }}>Deletar</Button>)}
        <Button
          style={{
            marginRight: 8,
          }}
          onClick={closeDrawerLeads}
        >
          Cancelar
        </Button>
        {get(data, 'id') ? (
          <Button onClick={handleSave} type="primary" loading={loading}>Salvar</Button>
        ) : (
          <Button onClick={handleCreate} type="primary" loading={loading}>Cadastrar</Button>
        )}
      </div>
    </Drawer>
  )
}

export default Form.create()(ProspectingDrawer);